import { FilterCategory } from 'src/app/core/models/filtering.interface';

/**
 * Project a stored selection (a saved view, or preserved table state) onto the currently available
 * filter categories.
 *
 * Selections whose option id is NOT in the available list are PRESERVED, never dropped:
 *  - options hidden from the panel on purpose (the built-in use-case labels);
 *  - options saved under an older vocabulary — e.g. the legacy Collaborators ids `Shared` / `Submitted`,
 *    replaced by NoResponse / PartialResponse / ReadyForReview.
 *
 * Dropping such an id silently turns a filtered view into an UNFILTERED one, because
 * `filterAssessments` skips any category with nothing selected. That is the Usercentrics saved-view
 * bug: the table honoured the stored `Submitted` and rendered 0 rows, while the rail counter dropped
 * it and reported the whole fleet.
 *
 * Shared by `AssessmentsTableService` (what the table renders) and `AssessmentsService` (what the rail
 * counter reports) so the two can never answer differently for the same view.
 */
export function mergeFilterSelections(available: FilterCategory[], incoming: FilterCategory[]): FilterCategory[] {
    const merged = available.map(filter => {
        const updatedFilter = incoming?.find(f => f?.id === filter?.id);
        const options = (filter.options ?? []).map(option => ({
            ...option,
            selected: updatedFilter?.options?.find(o => o?.id === option?.id)?.selected ?? false,
        }));
        // Selected options the available list doesn't know about — kept so the filter still applies.
        const extraSelected = (updatedFilter?.options ?? []).filter(
            option => option?.selected && !(filter.options ?? []).some(existing => existing?.id === option?.id),
        );
        return { ...filter, options: [...options, ...extraSelected] };
    });

    // Whole selected groups absent from the available list (hidden group, or not loaded yet when the
    // list was captured) — carried over for the same reason.
    const extraGroups = (incoming ?? []).filter(
        category => category?.options?.some(option => option?.selected)
            && !available.some(filter => filter?.id === category?.id),
    );

    return [...merged, ...extraGroups];
}
